import type { CSSProperties, ReactNode } from 'react'
import { apiJson } from '../../lib/api'

export interface AdminUser { id: number; username: string }

export async function apiSetupStatus(): Promise<{ needs_setup: boolean }> {
  return apiJson('/api/admin/setup/status')
}

export async function apiWhoami(): Promise<{ user: AdminUser | null }> {
  try {
    return await apiJson('/api/admin/whoami')
  } catch {
    return { user: null }
  }
}

export async function apiLogin(username: string, password: string) {
  return apiJson<{ ok: true; user: AdminUser }>('/api/admin/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  })
}

export async function apiSetup(username: string, password: string) {
  return apiJson<{ ok: true; user: AdminUser }>('/api/admin/setup', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  })
}

export async function apiLogout() {
  await apiJson('/api/admin/logout', { method: 'POST' })
}

export const ui = {
  shell: {
    minHeight: '100vh',
    background: 'var(--paper)',
    color: 'var(--fg)',
    fontFamily: 'var(--font-body)',
  } satisfies CSSProperties,
  card: {
    background: 'var(--pure)',
    border: '1px solid var(--line)',
    padding: 24,
  } satisfies CSSProperties,
  sectionTitle: {
    fontFamily: 'var(--font-display)', fontWeight: 500,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    fontSize: 22, margin: '0 0 16px',
  } satisfies CSSProperties,
  input: {
    width: '100%', padding: '10px 12px',
    fontFamily: 'var(--font-body)', fontSize: 14,
    background: 'var(--pure)', color: 'var(--fg)',
    border: '1px solid var(--line)', borderRadius: 2, outline: 'none',
    boxSizing: 'border-box',
  } satisfies CSSProperties,
  label: {
    display: 'block',
    fontFamily: 'var(--font-body)', fontSize: 11,
    letterSpacing: '0.2em', textTransform: 'uppercase',
    color: 'var(--fg-soft)', marginBottom: 6,
  } satisfies CSSProperties,
  btn: {
    fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 12,
    letterSpacing: '0.12em', textTransform: 'uppercase',
    padding: '10px 18px', border: 'none', cursor: 'pointer',
    background: 'var(--ink)', color: 'var(--bone)',
  } satisfies CSSProperties,
  btnGhost: {
    fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 12,
    letterSpacing: '0.12em', textTransform: 'uppercase',
    padding: '10px 18px', cursor: 'pointer',
    background: 'transparent', color: 'var(--fg)',
    border: '1px solid var(--fg)',
  } satisfies CSSProperties,
  btnDanger: {
    fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 12,
    letterSpacing: '0.12em', textTransform: 'uppercase',
    padding: '8px 14px', cursor: 'pointer',
    background: 'transparent', color: '#a33',
    border: '1px solid #a33',
  } satisfies CSSProperties,
}

export function Field({
  label, children,
}: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={ui.label}>{label}</span>
      {children}
    </label>
  )
}

export function BilingualField({
  label, valueIt, valueEn, onItChange, onEnChange, multiline,
}: {
  label: string
  valueIt: string
  valueEn: string
  onItChange: (v: string) => void
  onEnChange: (v: string) => void
  multiline?: boolean
}) {
  const inputProps = { style: ui.input }
  return (
    <div>
      <div style={ui.label}>{label}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 6 }}>
        <div>
          <span style={{ ...ui.label, marginBottom: 4 }}>IT</span>
          {multiline ? (
            <textarea rows={4} value={valueIt} onChange={(e) => onItChange(e.target.value)} {...inputProps} />
          ) : (
            <input value={valueIt} onChange={(e) => onItChange(e.target.value)} {...inputProps} />
          )}
        </div>
        <div>
          <span style={{ ...ui.label, marginBottom: 4 }}>EN</span>
          {multiline ? (
            <textarea rows={4} value={valueEn} onChange={(e) => onEnChange(e.target.value)} {...inputProps} />
          ) : (
            <input value={valueEn} onChange={(e) => onEnChange(e.target.value)} {...inputProps} />
          )}
        </div>
      </div>
    </div>
  )
}
